import {
	InvoicedBookingRepriceSelectionDto,
	RepriceDateMode,
} from 'Services/Api/_HumanWritten/InvoicedBookingRepriceService';

/**
 * UI-side model of "which invoiced bookings does this reprice target".
 *
 * The single source of truth the selection controls read from and write to; it maps to the
 * {@link InvoicedBookingRepriceSelectionDto} wire format via {@link toRepriceSelectionRequest}.
 *
 * Two axes, matching how operators describe the job:
 * - WHO — one or more invoiced customers, searched for individually and accumulated. Customer type
 *   is a search FILTER rather than a container, so one run can cover customers filed under several
 *   different types.
 * - WHEN — either the date the customer TRAVELS or the date they BOOKED, as a bounded range or
 *   open-ended. These are genuinely different questions: "everything sailing after the price rise"
 *   versus "everything booked before it".
 */
export interface RepriceSelection {
	/** The customers in scope. */
	userIds: string[];
	when: {
		mode: RepriceDateMode;
		startDate: string;
		/** Undefined/empty means open-ended. */
		endDate?: string;
	};
	options: {
		/**
		 * Include bookings carrying a manual staff price override. Off by default: the override is a
		 * flat dollar amount that survives a reprice untouched, so the customer lands on neither the
		 * negotiated total nor the new full price.
		 */
		includeAdjustedPrices: boolean;
		/**
		 * Price with dynamic pricing applied. Off by default — dynamic pricing reads capacity and
		 * lead time at evaluation time, so it makes the run non-repeatable.
		 */
		includeDynamicPricing: boolean;
	};
	/** A hand-picked subset of matched bookings; empty means the whole scope. */
	selectedBookingIds: string[];
}

/**
 * Default selection. Deliberately starts with NO date rather than today: the operator has to state
 * the window, because "from today" silently in a field they didn't look at is how an unintended
 * population gets repriced.
 */
export const defaultRepriceSelection = (): RepriceSelection => ({
	userIds: [],
	when: { mode: 'trip-date', startDate: '', endDate: '' },
	options: { includeAdjustedPrices: false, includeDynamicPricing: false },
	selectedBookingIds: [],
});

/** Maps the UI selection to the wire format, normalising an empty end date to open-ended. */
export function toRepriceSelectionRequest(selection: RepriceSelection): InvoicedBookingRepriceSelectionDto {
	return {
		userIds: selection.userIds,
		dateMode: selection.when.mode,
		startDate: selection.when.startDate,
		endDate: selection.when.endDate || null,
		includeAdjustedPrices: selection.options.includeAdjustedPrices,
		includeDynamicPricing: selection.options.includeDynamicPricing,
		selectedBookingIds: selection.selectedBookingIds,
	};
}

/**
 * Whether the selection is complete enough to preview. Mirrors the backend's own guards
 * (`InvoicedBookingRepriceSelectionQuery.Build`) so the operator gets told here rather than by a 400 —
 * but the backend keeps its guards regardless, because this one is only a convenience.
 */
export function isRepriceSelectionValid(selection: RepriceSelection): boolean {
	if (selection.userIds.length === 0) {
		return false;
	}
	if (!selection.when.startDate) {
		return false;
	}
	if (selection.when.endDate && selection.when.startDate > selection.when.endDate) {
		return false;
	}
	return true;
}

/**
 * Query-parameter names a handoff uses to seed the selection.
 *
 * Query params rather than router state so the link survives a reload, a bookmark or being pasted to a
 * colleague — a handoff that evaporates on refresh is worse than no handoff, because the operator has
 * already been told the work is queued up for them.
 */
export const REPRICE_PREFILL_PARAMS = {
	customers: 'customers',
} as const;

/**
 * Build the reprice URL a handoff should send the operator to. The customers are the ones the caller
 * just touched, NOT everyone of that type — re-deriving the type's membership would silently include
 * customers the caller deliberately left out.
 */
export function buildRepriceHandoffUrl(
	baseUrl: string,
	params: { userIds: string[] },
): string {
	const search = new URLSearchParams();
	if (params.userIds.length > 0) {
		search.set(REPRICE_PREFILL_PARAMS.customers, params.userIds.join(','));
	}
	const query = search.toString();
	return query ? `${baseUrl}?${query}` : baseUrl;
}

/**
 * Seed a selection from a handoff's query params. Only the WHO axis is prefilled — the date window is
 * left for the operator, because "which bookings are affected" is a judgement the handing-off feature
 * has no basis to make.
 */
export function repriceSelectionFromParams(search: URLSearchParams): RepriceSelection {
	const customers = search.get(REPRICE_PREFILL_PARAMS.customers);

	return {
		...defaultRepriceSelection(),
		userIds: customers ? customers.split(',').filter(Boolean) : [],
	};
}

/** A short sentence describing the selection, for the review step and confirmation copy. */
export function describeRepriceSelection(selection: RepriceSelection): string {
	const customers = `${selection.userIds.length} customer${selection.userIds.length === 1 ? '' : 's'}`;
	const dateLabel = selection.when.mode === 'trip-date' ? 'travelling' : 'booked';
	const window = selection.when.endDate
		? `between ${selection.when.startDate} and ${selection.when.endDate}`
		: `on or after ${selection.when.startDate}`;

	return `Invoiced bookings for ${customers}, ${dateLabel} ${window}`;
}
